import React from "react";

interface ProductColorImageProps {
    combinedItems: Array<{
        type: 'image' | 'color';
        id: string | number;
        colorCode: string;
        colorName: string;
        imageUrl?: string;
        imageName?: string;
        selected?: boolean;
        isMultiColor?: boolean;
        selectedColors?: any    }>;
    onItemClick?: (item: any) => void;
    showEditButton?: boolean;
    id: string;
    selectedItem?: { type: 'image' | 'color'; id: string | number } | null;
    selectedColorCode?: string; // New prop to pass the colorCode from variant
}

export const ProductColorImage: React.FC<ProductColorImageProps> = ({ 
    id, 
    combinedItems, 
    onItemClick,
    showEditButton = true,
    selectedItem,
    selectedColorCode
}) => {
    
    const getImageUrl = (url: string, type: string = 'productThumbnail') => {
        // This should match your existing getImageUrl function
        return url;
    };

    return (
        <div className="flex items-center justify-left flex-wrap gap-2" id={id}>
            {combinedItems?.map((item: any, index: number) => {
                // Check if item is selected based on selectedItem or selectedColorCode
                const isSelectedByItem = selectedItem?.type === item.type && selectedItem?.id === item.id;
                const isSelectedByColorCode = selectedColorCode && item.colorCode?.toLowerCase() === selectedColorCode?.toLowerCase();
                const isSelected = isSelectedByItem || isSelectedByColorCode;
                
                return (
                    <div 
                        key={`item-${item.type}-${index}`}
                        className="relative group"
                    >
                        {item.type === 'image-remove' ? (
                            // Image Container
                            <div className={`relative w-16 h-16 border-2 rounded-md overflow-hidden cursor-pointer transition-all duration-200 ${
                                isSelected 
                                    ? 'border-custom-500 bg-custom-50 dark:bg-custom-500/20 shadow-lg scale-105' 
                                    : 'border-slate-200 dark:border-zink-500 hover:border-custom-500 hover:scale-105'
                            }`}
                            onClick={() => onItemClick?.(item)}
                            >
                                <img 
                                    src={getImageUrl(item.imageUrl || item.preview)} 
                                    alt={item.imageName || `Product Image ${index + 1}`}
                                    className="w-full h-full object-cover"
                                />
                                
                                {/* Selection Indicator (small checkmark in top-right) */}
                                {isSelected && (
                                    <div className="absolute top-1 right-1 w-5 h-5 bg-custom-500 rounded-full flex items-center justify-center shadow-lg">
                                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                )}

                                {/* Color Indicator on Top */}
                                {item.colorCode && (
                                    <div 
                                        className="absolute top-1 left-1 w-4 h-4 rounded-full border-2 border-white shadow-sm"
                                        style={{ background: item.colorCode }}
                                        title={item.colorName}
                                    />
                                )}
                            </div>
                        ) : (
                            // Color Only Container
                            <div 
                                className={`relative w-16 h-16 border-2 rounded-md bg-slate-100 dark:bg-zink-600 flex items-center justify-center cursor-pointer transition-all duration-200 ${
                                    isSelected 
                                        ? 'border-custom-500 bg-custom-50 dark:bg-custom-500/20 shadow-lg scale-105' 
                                        : 'border-slate-200 dark:border-zink-500 hover:border-custom-500 hover:scale-105'
                                }`}
                                onClick={() => onItemClick?.(item)}
                            >
                                {/* Color Circle or Multi-Color Box */}
                                {item.isMultiColor ? (
                                    // Multi-Color Box
                                    <div className="multi-color-box-container">
                                        <div 
                                            className="color-container"
                                            style={{
                                                width: 32,
                                                height: 32,
                                                borderRadius: 5,
                                                border: '1px solid #ccc',
                                                marginBottom: 4
                                            }}
                                        >
                                            <div className="color-segments-container">
                                                {item.selectedColors?.map((color: any, colorIndex: number) => (
                                                    <div
                                                        key={colorIndex}
                                                        className="color-segment"
                                                        style={{
                                                            backgroundColor: color.colorCode,
                                                            borderRightWidth: colorIndex !== (item.selectedColors.length - 1) ? '0.5px' : '0',
                                                            borderRightColor: 'rgba(0, 0, 0, 0.2)'
                                                        }}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    // Single Color Circle
                                    <div 
                                        className={`w-8 h-8 rounded-full border-2 border-white shadow-sm transition-all duration-200 ${
                                            isSelected ? 'scale-110 ring-2 ring-custom-500' : ''
                                        }`}
                                        style={{ background: item.colorCode }}
                                        title={item.colorName}
                                    />
                                )}
                                
                                {/* Enhanced Selection Indicator */}
                                {isSelected && (
                                    <div className="absolute top-1 right-1 w-5 h-5 bg-custom-500 rounded-full flex items-center justify-center shadow-lg">
                                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Color Name on Bottom */}
                        <div className="mt-1 text-center">
                            <span className={`text-xs font-medium truncate block transition-colors duration-200 ${
                                isSelected 
                                    ? 'text-custom-600 dark:text-custom-400' 
                                    : 'text-slate-600 dark:text-zink-300'
                            }`}>
                                {item.colorName}
                            </span>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}; 